import moment from "moment";

// Rounds the tree can show, earliest first. The keys double as the scene's
// field prefixes (QuarterFinal_1_Team1_Name, ...), so they're named by size
// rather than by any one competition's round names.
export const TREE_ROUNDS = [
  { key: "RoundOf16", label: "Round of 16", ties: 8 },
  { key: "QuarterFinal", label: "Quarter-finals", ties: 4 },
  { key: "SemiFinal", label: "Semi-finals", ties: 2 },
  { key: "Final", label: "Final", ties: 1 },
];

// Round names differ between competitions and feeds (Opta's "Quarter-finals",
// DFL's German names), so they're only one way of spotting a round. Order
// matters: "Viertelfinale" and "Semi-finals" both contain "final".
const ROUND_NAME_PATTERNS = [
  {
    key: "RoundOf16",
    pattern: /round of 16|last 16|8th final|eighth|achtel|1\/8/i,
  },
  { key: "QuarterFinal", pattern: /quarter|viertel|1\/4/i },
  { key: "SemiFinal", pattern: /semi|halb|1\/2/i },
  { key: "Final", pattern: /^\s*finale?s?\s*$/i },
];

const GROUP_STAGE = /group|gruppe/i;

export function slotKey(roundKey, index) {
  return roundKey + "_" + (index + 1);
}

// Same status values the Fixtures graphic sends.
export function fixtureStatus(game) {
  let period_1 = game?.opta_periods?.find((p) => p.id === 1);
  let period_2 = game?.opta_periods?.find((p) => p.id === 2);
  return game?.opta_status === "Played"
    ? "result"
    : game?.opta_status === "Postponed"
      ? "postponed"
      : game?.opta_status === "Suspended"
        ? "suspended"
        : (!period_2 || !period_2.start) && period_1?.end
          ? "halftime"
          : period_1 && period_1.start
            ? "started"
            : "notstarted";
}

function kickOff(game) {
  return (
    moment(`${game?.date} ${game?.time}`, "YYYY-MM-DD HH:mmZ").valueOf() || 0
  );
}

function byKickOff(a, b) {
  return kickOff(a) - kickOff(b);
}

function groupBy(items, keyOf) {
  let groups = {};
  items.forEach((item) => {
    let key = keyOf(item);
    (groups[key] = groups[key] || []).push(item);
  });
  return Object.values(groups);
}

function isPair(game, a, b) {
  let home = game?.home_team?._id;
  let away = game?.away_team?._id;
  return (home === a && away === b) || (home === b && away === a);
}

// Same convention as the Fixtures buttons: total includes any shootout.
function goals(game, side) {
  return Math.max(
    0,
    (Number(game?.opta_scores?.total?.[side]) || 0) -
      (Number(game?.opta_scores?.pen?.[side]) || 0),
  );
}

// A tie is every game between the same two teams, so both legs of a
// two-legged tie count once. A game still missing a team stands alone.
function toTies(games) {
  return groupBy(games, (g) =>
    g?.home_team?._id && g?.away_team?._id
      ? [g.home_team._id, g.away_team._id].sort().join("|")
      : "game:" + g?._id,
  ).map((tieGames) => {
    let legs = [...tieGames].sort(byKickOff);
    return { legs, team1: legs[0]?.home_team, team2: legs[0]?.away_team };
  });
}

// In a knockout round every team plays a single tie; in a group stage,
// whatever it's called, teams face several opponents.
function isKnockoutRound(ties) {
  let seen = new Set();
  return ties.every((tie) =>
    [tie.team1?._id, tie.team2?._id].filter(Boolean).every((id) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }),
  );
}

// Splits a season's games into knockout rounds and works out which tree round
// each is. Games are grouped by stage; a stage that holds several rounds (a
// feed that labels every cup round alike) is split by week/matchday instead.
export function analyseKnockout(fixtures) {
  let knockoutGames = (fixtures || []).filter(
    (f) => !GROUP_STAGE.test(f?.stage ?? ""),
  );

  let candidates = [];
  groupBy(knockoutGames, (g) => g?.stage ?? "").forEach((stageGames) => {
    let stage = stageGames[0]?.stage ?? "";
    let ties = toTies(stageGames);
    if (isKnockoutRound(ties)) {
      candidates.push({ stage, ties, games: stageGames });
      return;
    }
    groupBy(stageGames, (g) => g?.week ?? "").forEach((weekGames) => {
      let weekTies = toTies(weekGames);
      if (isKnockoutRound(weekTies)) {
        candidates.push({ stage, ties: weekTies, games: weekGames });
      }
    });
  });

  // Latest first, so if two rounds are the same size the one nearer the final
  // takes the slot.
  candidates.sort(
    (a, b) =>
      Math.min(...b.games.map(kickOff)) - Math.min(...a.games.map(kickOff)),
  );

  let rounds = {};
  let assign = (candidate, key) => {
    if (!key || rounds[key]) return;
    rounds[key] = candidate.ties;
    candidate.assigned = true;
  };
  // A stage called "Semi-finals" is trusted over its tie count, which a
  // missing fixture can throw off. The count covers the names we can't read
  // ("3rd Round", DFL matchdays).
  candidates.forEach((c) =>
    assign(c, ROUND_NAME_PATTERNS.find((p) => p.pattern.test(c.stage))?.key),
  );
  candidates.forEach(
    (c) =>
      !c.assigned &&
      assign(c, TREE_ROUNDS.find((r) => r.ties === c.ties.length)?.key),
  );

  let roundOfGame = new Map();
  Object.entries(rounds).forEach(([key, ties]) =>
    ties.forEach((tie) => tie.legs.forEach((g) => roundOfGame.set(g._id, key))),
  );

  let teams = new Map();
  (fixtures || []).forEach((g) =>
    [g?.home_team, g?.away_team].forEach((t) => t?._id && teams.set(t._id, t)),
  );

  return { rounds, roundOfGame, knockoutGames, teams };
}

// Fills every slot with a { team1, team2 } pair of team ids, working back from
// the final so the tree's lines join up: the earlier tie holding a later tie's
// Team1 sits directly above the one holding its Team2. Kick-off dates never
// decide that order, as draws don't follow the calendar. Operator edits are
// kept as they are and steer the rounds below them, and ties not linked to the
// next round yet (not drawn, or a team still unknown) fill the gaps in kick-off
// order.
export function buildTree(analysis, overrides = {}) {
  let tree = {};
  let above = null;
  [...TREE_ROUNDS].reverse().forEach((round) => {
    let pool = [...(analysis.rounds[round.key] || [])].sort((a, b) =>
      byKickOff(a.legs[0], b.legs[0]),
    );
    let take = (teamId) => {
      let i = teamId
        ? pool.findIndex(
            (t) => t.team1?._id === teamId || t.team2?._id === teamId,
          )
        : -1;
      return i > -1 ? pool.splice(i, 1)[0] : null;
    };
    let pairOf = (tie) => ({
      team1: tie.team1?._id ?? null,
      team2: tie.team2?._id ?? null,
    });

    let slots = new Array(round.ties).fill(undefined);
    // Edited teams leave the pool so they can't turn up in a second slot.
    slots.forEach((_, index) => {
      let edit = overrides[slotKey(round.key, index)];
      if (edit) {
        slots[index] = edit;
        take(edit.team1);
        take(edit.team2);
      }
    });
    above?.forEach((slot, k) => {
      [slot?.team1, slot?.team2].forEach((teamId, side) => {
        let index = 2 * k + side;
        if (slots[index] !== undefined) return;
        let tie = take(teamId);
        if (tie) slots[index] = pairOf(tie);
      });
    });
    slots = slots.map((slot) =>
      slot !== undefined ? slot : pool.length ? pairOf(pool.shift()) : null,
    );

    tree[round.key] = slots;
    above = slots;
  });
  return tree;
}

// Looks a slot's games up from its two teams, so a slot set by hand still
// gets its scores. Team1's goals are read from whichever side it played on in
// each leg.
function resolveTie(analysis, roundKey, slot) {
  if (!slot?.team1 && !slot?.team2) return null;

  let games = [];
  if (slot.team1 && slot.team2) {
    let pairGames = analysis.knockoutGames
      .filter((g) => isPair(g, slot.team1, slot.team2))
      .sort(byKickOff);
    let inRound = pairGames.filter(
      (g) => analysis.roundOfGame.get(g._id) === roundKey,
    );
    // If round detection missed it, fall back to the pair's latest meeting.
    let last = pairGames[pairGames.length - 1];
    games = inRound.length
      ? inRound
      : pairGames.filter((g) => g.stage === last?.stage);
  }

  let legs = games.map((game) => {
    let team1Side = game.home_team?._id === slot.team1 ? "home" : "away";
    let team2Side = team1Side === "home" ? "away" : "home";
    let pen = game.opta_scores?.pen;
    let status = fixtureStatus(game);
    return {
      game,
      status,
      // Kicked off, so its score means something rather than an unplayed 0.
      started: status !== "notstarted" && status !== "postponed",
      team1Goals: goals(game, team1Side),
      team2Goals: goals(game, team2Side),
      team1Pens: pen ? Number(pen[team1Side]) || 0 : null,
      team2Pens: pen ? Number(pen[team2Side]) || 0 : null,
    };
  });
  // The leg still to finish, or the last one once they're all played.
  let current =
    legs.find((leg) => leg.status !== "result") ?? legs[legs.length - 1];
  let shootout = [...legs].reverse().find((leg) => leg.team1Pens !== null);

  return {
    team1: analysis.teams.get(slot.team1) ?? null,
    team2: analysis.teams.get(slot.team2) ?? null,
    legs,
    started: legs.some((leg) => leg.started),
    team1Score: legs.reduce((sum, leg) => sum + leg.team1Goals, 0),
    team2Score: legs.reduce((sum, leg) => sum + leg.team2Goals, 0),
    team1Pens: shootout?.team1Pens ?? null,
    team2Pens: shootout?.team2Pens ?? null,
    status: current?.status ?? "",
    date: current?.game?.date ?? "",
    time: current?.game?.time ?? "",
    winner: null,
  };
}

// Whoever appears in the next round's slot went through; failing that, the
// result decides once every leg is played.
function winnerOf(tie, nextSlot) {
  let through = [nextSlot?.team1, nextSlot?.team2];
  if (tie.team1 && through.includes(tie.team1._id)) return 1;
  if (tie.team2 && through.includes(tie.team2._id)) return 2;
  if (!tie.legs.length || tie.legs.some((leg) => leg.status !== "result")) {
    return null;
  }
  if (tie.team1Score !== tie.team2Score) {
    return tie.team1Score > tie.team2Score ? 1 : 2;
  }
  if (tie.team1Pens !== null && tie.team1Pens !== tie.team2Pens) {
    return tie.team1Pens > tie.team2Pens ? 1 : 2;
  }
  return null;
}

export function resolveTree(analysis, tree) {
  let resolved = {};
  TREE_ROUNDS.forEach((round, r) => {
    let next = TREE_ROUNDS[r + 1];
    resolved[round.key] = tree[round.key].map((slot, index) => {
      let tie = resolveTie(analysis, round.key, slot);
      if (tie) {
        tie.winner = winnerOf(tie, next && tree[next.key][Math.floor(index / 2)]);
      }
      return tie;
    });
  });
  return resolved;
}
